import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button, ProductImageCarousel, QuantitySelector } from '../components/ui';
import { publicApi } from '../services/api';
import {
  getCachedProductDetail,
  loadProductDetail,
} from '../services/publicCatalogCache';
import { trackCartEvent, trackSilentFailure, trackProductViewed } from '../services/analytics';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useCart();
  const trackedProductIdRef = useRef(null);
  const seededProduct = (() => {
    const cachedProduct = getCachedProductDetail(id)?.data;
    return cachedProduct || location.state?.productPreview || null;
  })();
  const [product, setProduct] = useState(seededProduct);
  const [loading, setLoading] = useState(!seededProduct);
  const [quantity, setQuantity] = useState(1);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    setProduct(seededProduct);
    setLoading(!seededProduct);
    setLoadError('');
    trackedProductIdRef.current = null;
  }, [id, seededProduct]);

  const loadProduct = useCallback(async () => {
    let isMounted = true;

    try {
      const cached = getCachedProductDetail(id);
      if (!cached?.data) {
        setLoading(true);
      }

      const result = await loadProductDetail(id, async () => {
        const res = await publicApi.get(`/products/${id}`);
        return res.data;
      });

      if (!isMounted) {
        return () => {};
      }

      setProduct(result.data);
      setLoadError('');

      if (result.revalidate) {
        void result.revalidate
          .then((nextProduct) => {
            if (!isMounted) {
              return;
            }
            setProduct(nextProduct);
            setLoadError('');
          })
          .catch((err) => {
            if (!isMounted) {
              return;
            }
            console.error('Failed to refresh product detail:', err);
          });
      }
    } catch (err) {
      console.error('Failed to fetch product:', err);
      if (isMounted) {
        if (!seededProduct) {
          setProduct(null);
        }
        if (err.response?.status === 404) {
          setLoadError('This product is no longer available.');
        } else if (!seededProduct) {
          setLoadError(
            err.response?.data?.message ||
            'Failed to load this product right now.'
          );
        }
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
    
    return () => {
      isMounted = false;
    };
  }, [id, seededProduct]);

  useEffect(() => {
    let cleanup;
    void (async () => {
      cleanup = await loadProduct();
    })();

    return () => {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [loadProduct]);

  useEffect(() => {
    const trackedProductId = product?._id || product?.id;

    if (!trackedProductId || trackedProductIdRef.current === trackedProductId) {
      return;
    }

    trackedProductIdRef.current = trackedProductId;

    trackProductViewed({
      id: trackedProductId,
      name: product.name,
      price: product.price,
    });
  }, [product]);

  const handleAddToCart = () => {
    if (product) {
      trackCartEvent({
        action: 'add_to_cart',
        status: 'started',
        productId: product._id || product.id,
        metadata: {
          product_name: product.name,
          quantity,
        },
      });

      try {
        dispatch({ type: 'ADD', payload: { product, qty: quantity } });
        
        trackCartEvent({
          action: 'add_to_cart',
          status: 'succeeded',
          productId: product._id || product.id,
          cartValue: product.price * quantity,
          metadata: {
            product_name: product.name,
            quantity,
          },
        });
        
        navigate('/cart');
      } catch (err) {
        trackCartEvent({
          action: 'add_to_cart',
          status: 'failed',
          productId: product._id || product.id,
          errorMessage: err.message,
        });
        
        trackSilentFailure({
          context: 'add_to_cart',
          expectedState: 'item_added',
          actualState: 'exception_thrown',
          metadata: { error: err.message },
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-light-secondary dark:bg-brand-dark transition-colors duration-200">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-light-secondary dark:bg-brand-dark transition-colors duration-200">
        <p className="text-[18px] text-light-text-secondary dark:text-brand-text-secondary mb-4">
          {loadError || 'Product not found'}
        </p>
        <button
          type="button"
          onClick={() => void loadProduct()}
          className="mb-4 inline-flex items-center justify-center rounded-[12px] bg-black px-[18px] py-[12px] text-[14px] font-semibold text-white transition hover:bg-black/90"
        >
          Try Again
        </button>
        <Link to="/catalog" className="text-brand-accent-red hover:underline">
          Back to Catalog
        </Link>
      </div>
    );
  }

  const available = Number.isFinite(Number(product.inventory ?? product.stock))
    ? Math.max(0, Number(product.inventory ?? product.stock))
    : undefined;
  const productImages = Array.isArray(product.images) && product.images.length > 0
    ? product.images
    : [product.imageUrl || product.image_url || product.image].filter(Boolean);

  return (
    <main className="bg-light-secondary dark:bg-brand-dark min-h-screen transition-colors duration-200">
      <div className="max-w-[1200px] mx-auto px-[16px] md:px-[24px] py-[12px] md:py-[40px]">
        {/* Back to Catalog */}
        <Link
          to="/catalog"
          className="inline-flex items-center gap-2 text-[13px] md:text-[14px] text-light-text-secondary dark:text-brand-text-secondary hover:text-brand-accent-red mb-[12px] md:mb-[24px] transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Catalog
        </Link>

        {/* Product Detail Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] md:gap-[48px] items-start">
          {/* Product Image */}
          <div className="w-full max-w-[520px] mx-auto md:mx-0">
            <div className="aspect-[4/3] overflow-hidden rounded-[18px] border border-[#E5E5E5] bg-white shadow-sm dark:border-brand-border-default dark:bg-brand-surface sm:aspect-[5/4] md:aspect-auto md:rounded-[16px] md:border-0 md:bg-white md:p-[24px] md:shadow-[0px_4px_12px_rgba(0,0,0,0.06)] md:dark:bg-brand-elevated">
              <div className="relative h-full w-full md:aspect-square md:overflow-hidden md:rounded-[16px] md:bg-light-secondary md:dark:bg-brand-surface">
                <ProductImageCarousel
                  images={productImages}
                  alt={product.name}
                  imageClassName="w-full h-full object-cover md:object-contain"
                  showArrows={productImages.length > 1}
                  showDots={productImages.length > 1}
                  loading="eager"
                  autoPlay={productImages.length > 1}
                  autoPlayInterval={4500}
                />
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div className="flex flex-col gap-[14px] md:gap-[24px] max-w-[480px]">
            <div>
              <h1 className="text-[22px] sm:text-[24px] md:text-[32px] font-bold text-light-text-primary dark:text-brand-text-primary mb-[6px] md:mb-[12px] leading-tight">
                {product.name}
              </h1>
              <p className="text-[14px] md:text-[16px] text-light-text-secondary dark:text-brand-text-secondary leading-[1.45] md:leading-relaxed">
                {product.description || 'Premium eyewear designed for performance and style.'}
              </p>
            </div>

            <div className="border-t border-[#E5E5E5] dark:border-brand-border-default pt-[14px] md:pt-[24px]">
              <p className="text-[20px] sm:text-[22px] md:text-[32px] font-bold text-light-text-primary dark:text-brand-text-primary">
                UGX {product.price.toLocaleString()}
              </p>
            </div>

            {available !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-[13px] md:text-[14px] text-light-text-secondary dark:text-brand-text-secondary">
                  Stock:
                </span>
                <span className={`text-[13px] md:text-[14px] font-semibold ${available > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {available > 0 ? `${available} available` : 'Out of stock'}
                </span>
              </div>
            )}

            <div className="flex flex-col gap-[12px] md:gap-[16px] pt-[4px] md:pt-[8px]">
              <div className="flex items-center gap-[12px] md:gap-[16px]">
                <span className="text-[13px] md:text-[14px] text-light-text-secondary dark:text-brand-text-secondary">
                  Quantity:
                </span>
                <QuantitySelector
                  quantity={quantity}
                  onQuantityChange={setQuantity}
                  max={available}
                />
              </div>

              <Button
                onClick={handleAddToCart}
                disabled={!available || available === 0}
                className="w-full h-[48px] md:h-[56px] text-[15px] md:text-[16px] rounded-[12px] bg-black text-white font-semibold hover:bg-black/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add to Cart
              </Button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
